/** Mirrors of the C# Applicants DTO records, field for field. */

/** One job posting — a job-posting tag and the plantilla item it advertises. */
export interface JobPostingListItem {
  /** The posting (the tag) — what applicants attach to. */
  id: string;
  plantillaItemId: string;
  itemNumber: string;
  plantillaPositionId: string;
  positionTitle: string;
  /** The post's office — Deliberate's Assignment defaults to it. */
  orgUnitId: string;
  orgUnitName: string;
  salaryGradeLabel: string | null;
  salaryGrade: number | null;
  monthlyAmount: number | null;
  /** Plain text — render as text, never markup. */
  education: string | null;
  training: string | null;
  experience: string | null;
  eligibilityName: string | null;
  otherExpectations: string | null;
  /** Live applicants. */
  applicantCount: number;
  /** False once the tag was removed or the item filled. Its applicants are kept. */
  isActive: boolean;
  /** PUBLISHED — until it is, the posting takes no applicant and Deliberate does not list it. */
  isPublished: boolean;
  /**
   * COMPLETED — its endorsed applicant's APPOINTMENT is recorded, the oath taking or the assignment (Deliberate,
   * 22 Oct 2026). Derived; a withdrawal reverts it. A completed posting takes no new applicants.
   */
  isCompleted: boolean;
  /** A live applicant on it is ENDORSED — it takes no second endorsement. Derived. */
  hasEndorsement: boolean;
  /**
   * ENDED and FILLED — the item is held now and no later posting was made for it, so the holder is read as
   * this posting's appointment (Deliberate). Derived, read off the item.
   */
  isFilled: boolean;
  /** The POSTING's provenance — when the item was tagged. */
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/**
 * Where an application stands. The wire enum is NUMERIC; the label comes from the server
 * (`statusLabel`). Mirrors `ApplicantStatus` member for member — a member added there is added here
 * in the same change.
 */
export const APPLICANT_STATUS = {
  Received: 0,
  Qualified: 1,
  ForAssessment: 2,
  ForDeliberation: 3,
  Selected: 4,
  NotQualified: 5,
  NotSelected: 6,
} as const;

export type ApplicantStatusValue = (typeof APPLICANT_STATUS)[keyof typeof APPLICANT_STATUS];

/** The picker's words, in the stage order the product owner gave (15 Oct 2026). */
export const APPLICANT_STATUS_OPTIONS: { value: ApplicantStatusValue; label: string }[] = [
  { value: APPLICANT_STATUS.Received, label: 'Received' },
  { value: APPLICANT_STATUS.Qualified, label: 'Qualified' },
  { value: APPLICANT_STATUS.ForAssessment, label: 'For Assessment' },
  { value: APPLICANT_STATUS.ForDeliberation, label: 'For Deliberation' },
  { value: APPLICANT_STATUS.Selected, label: 'Selected' },
  { value: APPLICANT_STATUS.NotQualified, label: 'Not Qualified' },
  { value: APPLICANT_STATUS.NotSelected, label: 'Not Selected' },
];

/** One applicant. **No name** — resolve `personId` through `useApplicantNames`. */
export interface ApplicantListItem {
  id: string;
  jobPostingTagId: string;
  personId: string;
  email: string | null;
  mobileNumber: string | null;
  /** A calendar date (`YYYY-MM-DD`) — `formatDateOnly`, never the instant path. */
  dateApplied: string;
  status: ApplicantStatusValue;
  statusLabel: string;
  remarks: string | null;
  // Initial Screening — absent until screened.
  screeningResult: ApplicantQualificationValue | null;
  screeningResultLabel: string | null;
  screenedByUserId: string | null;
  screenedByName: string | null;
  /** A calendar date. */
  screenedDate: string | null;
  screeningRemarks: string | null;
  // Initial Interview — absent until scheduled.
  interviewPlace: string | null;
  /** A calendar date. */
  interviewDate: string | null;
  /** A time of day (`HH:mm:ss`) — `formatClockTime`, never an instant. */
  interviewTime: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** Records or corrects one applicant. The name goes to the identity store; no status here. */
export interface ApplicantInput {
  salutation: string | null;
  firstName: string;
  middleName: string | null;
  lastName: string;
  suffix: string | null;
  email: string | null;
  mobileNumber: string | null;
  dateApplied: string;
  remarks: string | null;
}

/** The name's parts, for the edit form — one applicant at a time, audited. */
export interface ApplicantName {
  salutation: string | null;
  firstName: string;
  middleName: string | null;
  lastName: string;
  suffix: string | null;
}

/**
 * The stages the status picker may set — everything AFTER screening. Received, Qualified and Not
 * Qualified are screening's (`ApplicantStatuses.IsPickable` on the server — the same rule twice).
 */
export const PICKABLE_APPLICANT_STATUS_OPTIONS = APPLICANT_STATUS_OPTIONS.filter(
  (o) =>
    o.value !== APPLICANT_STATUS.Received &&
    o.value !== APPLICANT_STATUS.Qualified &&
    o.value !== APPLICANT_STATUS.NotQualified,
);

/** The outcome of Initial Screening. NUMERIC on the wire; mirrors `ApplicantQualification`. */
export const APPLICANT_QUALIFICATION = {
  Qualified: 0,
  Disqualified: 1,
} as const;

export type ApplicantQualificationValue = (typeof APPLICANT_QUALIFICATION)[keyof typeof APPLICANT_QUALIFICATION];

export const APPLICANT_QUALIFICATION_OPTIONS: { value: ApplicantQualificationValue; label: string }[] = [
  { value: APPLICANT_QUALIFICATION.Qualified, label: 'Qualified' },
  { value: APPLICANT_QUALIFICATION.Disqualified, label: 'Disqualified' },
];

/** One line of the screening checklist. */
export interface ApplicantChecklistItem {
  requirementId: string;
  name: string;
  description: string | null;
  /** False on a requirement deleted from Settings — listed only because it was answered. */
  isActive: boolean;
  /** Complied? Yes needs a file on this line (22 Oct 2026). */
  requiresAttachment: boolean;
  /** Complied? as recorded. Absent (`undefined` on the wire) = not answered yet. */
  isComplied: boolean | null;
  /** The submitted file's own name. Absent with no file. */
  attachmentFileName: string | null;
}

/** One answered checklist line on a screening request. */
export interface ApplicantChecklistAnswer {
  requirementId: string;
  isComplied: boolean;
  /** A file for this line uploads right after the screening saves — what lets Yes stand where one is required. */
  attachmentToFollow: boolean;
}

/** The screening so far, and the checklist to tick. */
export interface ApplicantScreening {
  screeningResult: ApplicantQualificationValue | null;
  screenedByUserId: string | null;
  screenedDate: string | null;
  screeningRemarks: string | null;
  checklist: ApplicantChecklistItem[];
}

export interface ApplicantScreeningRequest {
  screeningResult: ApplicantQualificationValue;
  screenedByUserId: string;
  screenedDate: string;
  screeningRemarks: string | null;
  /** The WHOLE checklist answered — every live requirement Yes or No. Files ride on their own request. */
  requirements: ApplicantChecklistAnswer[];
}

export interface ApplicantInterviewRequest {
  interviewPlace: string;
  interviewDate: string;
  /** `HH:mm:00`. */
  interviewTime: string;
}

/** One row of the applicant-requirement catalog. */
export interface ApplicantRequirementListItem {
  id: string;
  name: string;
  description: string | null;
  /** Complied? Yes needs a file attached to the line. */
  requiresAttachment: boolean;
  /** Live applicants who complied with it. */
  checkedCount: number;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

export interface ApplicantRequirementRequest {
  name: string;
  description: string | null;
  requiresAttachment: boolean;
}

/**
 * The assessment stages, in the product owner's order (20 Oct 2026). NUMERIC on the wire; mirrors
 * `ApplicantAssessmentStage` member for member. None gates another.
 */
export const APPLICANT_ASSESSMENT_STAGE = {
  FinalInterview: 0,
  WrittenExam: 1,
  NeuroPsychiatric: 2,
  Medical: 3,
  BackgroundInterview: 4,
} as const;

export type ApplicantAssessmentStageValue = (typeof APPLICANT_ASSESSMENT_STAGE)[keyof typeof APPLICANT_ASSESSMENT_STAGE];

/**
 * Every stage with its tab key and label, in order. The label is the server's `StageLabel` spelled
 * once more, because the TAB is drawn before the stages arrive.
 */
export const APPLICANT_ASSESSMENT_STAGES = [
  { value: APPLICANT_ASSESSMENT_STAGE.FinalInterview, key: 'final-interview', label: 'Final Interview', isHealth: false },
  { value: APPLICANT_ASSESSMENT_STAGE.WrittenExam, key: 'written-exam', label: 'Written Exam', isHealth: false },
  { value: APPLICANT_ASSESSMENT_STAGE.NeuroPsychiatric, key: 'neuro-psychiatric', label: 'Neuro-Psychiatric', isHealth: true },
  { value: APPLICANT_ASSESSMENT_STAGE.Medical, key: 'medical', label: 'Medical', isHealth: true },
  { value: APPLICANT_ASSESSMENT_STAGE.BackgroundInterview, key: 'background-interview', label: 'Background Interview', isHealth: false },
] as const satisfies readonly { value: ApplicantAssessmentStageValue; key: string; label: string; isHealth: boolean }[];

/** Reading, recording and downloading a HEALTH stage — Neuro-Psychiatric and Medical. */
export const APPLICANT_HEALTH_PERMISSION = 'applicants.read-medical';

/** Mirrors `ApplicantAssessmentResult`. */
export const APPLICANT_ASSESSMENT_RESULT = {
  Passed: 0,
  Failed: 1,
} as const;

export type ApplicantAssessmentResultValue = (typeof APPLICANT_ASSESSMENT_RESULT)[keyof typeof APPLICANT_ASSESSMENT_RESULT];

export const APPLICANT_ASSESSMENT_RESULT_OPTIONS: { value: ApplicantAssessmentResultValue; label: string }[] = [
  { value: APPLICANT_ASSESSMENT_RESULT.Passed, label: 'Passed' },
  { value: APPLICANT_ASSESSMENT_RESULT.Failed, label: 'Failed' },
];

/** One stage of one applicant, recorded or not. */
export interface ApplicantAssessment {
  stage: ApplicantAssessmentStageValue;
  stageLabel: string;
  isHealth: boolean;
  /** A health stage this caller may not read — every value below is then absent. */
  isHidden: boolean;
  /** Absent until recorded. */
  result: ApplicantAssessmentResultValue | null;
  resultLabel: string | null;
  /** Sanitised HTML — render through `<RichTextDocument>` only. */
  remarks: string | null;
  attachments: ApplicantAssessmentAttachment[];
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface ApplicantAssessmentAttachment {
  id: string;
  fileName: string;
  createdAt: string;
  createdByName: string | null;
}

export interface ApplicantAssessmentRequest {
  result: ApplicantAssessmentResultValue;
  remarks: string | null;
}

/** Score Points — each stage's weight (%). Unconfigured until somebody saves them. */
export interface ApplicantScoreWeights {
  isConfigured: boolean;
  weights: { stage: ApplicantAssessmentStageValue; stageLabel: string; weight: number | null }[];
  createdAt: string | null;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface ApplicantScoreWeightsRequest {
  weights: { stage: ApplicantAssessmentStageValue; weight: number | null }[];
}

/** What the module writes, for its History Logs. A rename keeps the old CLR name here forever. */
export const APPLICANT_AUDIT_ENTITY_TYPES = [
  'Applicant',
  'ApplicantRequirementCheck',
  'ApplicantAssessment',
  'ApplicantAssessmentAttachment',
];

/** Score Points' own History Logs. */
export const APPLICANT_SCORE_WEIGHTS_AUDIT_ENTITY_TYPES = ['ApplicantScoreWeights'];

/** The requirement catalog's History Logs. */
export const APPLICANT_REQUIREMENT_AUDIT_ENTITY_TYPES = ['ApplicantRequirement'];
